import {
    ArrowRightOutlined,
    BulbOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    FilterOutlined,
    MinusSquareOutlined,
    PlusOutlined,
    PlusSquareOutlined,
    TagOutlined,
    UpOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { Badge, Button, Checkbox, Modal, Spin, Tabs, Tag, Tooltip, message } from "antd";
import React, { Component } from "react";
//added changes
const CONFIDENCE_THRESHOLDS = {
    HIGH: 0.8,
    MEDIUM: 0.6,
};

const TAG_SECTION_CONFIG = {
    additionalTags: {
        title: "Recommended Additional Tags",
        icon: <CheckCircleOutlined className="text-lg text-green-600" />,
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-700",
    },
    mismatchedTags: {
        title: "Tags to Review",
        icon: <ExclamationCircleOutlined className="text-lg text-yellow-600" />,
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        textColor: "text-yellow-700",
        actionText: "Remove",
    },
    newTagRecommendations: {
        title: "New Tag Suggestions",
        icon: <BulbOutlined className="text-lg text-blue-600" />,
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        textColor: "text-blue-700",
    },
};

const EMPTY_MESSAGES = {
    all: "No suggestions available for any fields.",
    additional: "No fields have additional tag recommendations.",
    mismatched: "No incorrect tags found - all tags look good!",
    new: "No new tag suggestions available.",
};
class AutoTaggingModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedSuggestions: this.initializeSelectedSuggestions(this.getSuggestions(props.suggestions)),
            loading: false,
            expandedFields: new Set(),
            activeTab: "all",
            showCreationConfirmation: false,
            showCreationSuccess: false,
            isCreatingTags: false,
            tagsToCreate: [],
            duplicatesSkipped: [],
            similarWarnings: [],
            createdTags: new Set(),
            justCreatedTagNames: [],
        };
        this.successTimeoutId = null;
    }

    componentWillUnmount() {
        if (this.successTimeoutId) {
            clearTimeout(this.successTimeoutId);
        }
    }
    componentDidUpdate(prevProps) {
        if (prevProps.suggestions !== this.props.suggestions) {
            this.setState({
                selectedSuggestions: this.initializeSelectedSuggestions(this.getSuggestions(this.props.suggestions)),
            });
        }

        if (prevProps.visible && !this.props.visible) {
            this.resetAllStates();
        }

        if (this.props.bulkCreateResult !== prevProps.bulkCreateResult && this.state.isCreatingTags) {
            this.handleBulkCreateResultUpdate();
        }
    }

    handleBulkCreateResultUpdate = () => {
        if (this.props.bulkCreateResult.success) {
            const createdTags = this.props.bulkCreateTagsResult || [];

            if (createdTags.length > 0) {
                this.handleSuccessfulTagCreation(createdTags);
            } else {
                this.setState({ isCreatingTags: false });
            }
        } else if (this.props.bulkCreateResult.success === false) {
            this.setState({
                isCreatingTags: false,
                showCreationConfirmation: false,
            });
        }
    };

    handleSuccessfulTagCreation = (createdTags) => {
        const createdTagNames = createdTags.map((t) => t.tagName);
        const updatedSelections = this.moveCreatedTagsToAdditional(createdTagNames);

        if (this.successTimeoutId) {
            clearTimeout(this.successTimeoutId);
        }

        this.setState({
            showCreationConfirmation: false,
            showCreationSuccess: true,
            isCreatingTags: false,
            createdTags: new Set([...this.state.createdTags, ...createdTagNames]),
            justCreatedTagNames: createdTagNames,
            selectedSuggestions: updatedSelections,
        });

        this.successTimeoutId = setTimeout(() => {
            this.setState({ showCreationSuccess: false });
        }, 8000);
    };

    resetAllStates = () => {
        if (this.successTimeoutId) {
            clearTimeout(this.successTimeoutId);
            this.successTimeoutId = null;
        }
        this.setState({
            selectedSuggestions: {},
            loading: false,
            expandedFields: new Set(),
            activeTab: "all",
            showCreationConfirmation: false,
            showCreationSuccess: false,
            isCreatingTags: false,
            tagsToCreate: [],
            duplicatesSkipped: [],
            similarWarnings: [],
            createdTags: new Set(),
            justCreatedTagNames: [],
        });
    };
    getSuggestions = (suggestions) => {
        if (!suggestions) return [];
        if (suggestions.suggestions) {
            return suggestions.suggestions;
        }
        if (suggestions.data && suggestions.data.suggestions) {
            return suggestions.data.suggestions;
        }
        return [];
    };

    getSummary = (suggestions) => {
        if (!suggestions) return null;
        if (suggestions.summary) {
            return suggestions.summary;
        }
        if (suggestions.data && suggestions.data.summary) {
            return suggestions.data.summary;
        }
        return null;
    };

    initializeSelectedSuggestions = (suggestions) => {
        if (!suggestions || !Array.isArray(suggestions)) return {};

        const selected = {};
        suggestions.forEach((suggestion) => {
            selected[suggestion.fieldId] = {
                additionalTags: (suggestion.additionalTags || []).reduce((acc, tag) => {
                    acc[tag.tagName] = true;
                    return acc;
                }, {}),
                mismatchedTags: {},
                newTagRecommendations: {},
            };
        });
        return selected;
    };

    getFilteredSuggestions = () => {
        const suggestions = this.getSuggestions(this.props.suggestions);
        const { activeTab } = this.state;

        if (activeTab === "all") return suggestions;

        const filterMap = {
            additional: (s) => s.additionalTags && s.additionalTags.length > 0,
            mismatched: (s) => s.mismatchedTags && s.mismatchedTags.length > 0,
            new: (s) => s.newTagRecommendations && s.newTagRecommendations.length > 0,
        };

        return suggestions.filter(filterMap[activeTab] || (() => true));
    };

    getCategoryCounts = () => {
        const suggestions = this.getSuggestions(this.props.suggestions);

        const counts = {
            all: suggestions.length,
            additional: 0,
            mismatched: 0,
            new: 0,
        };

        suggestions.forEach((suggestion) => {
            if (suggestion.additionalTags && suggestion.additionalTags.length > 0) {
                counts.additional++;
            }
            if (suggestion.mismatchedTags && suggestion.mismatchedTags.length > 0) {
                counts.mismatched++;
            }
            if (suggestion.newTagRecommendations && suggestion.newTagRecommendations.length > 0) {
                counts.new++;
            }
        });

        return counts;
    };

    // TAG VALIDATION
    checkForDuplicates = (tagName) => {
        const existingTags = (this.props.existingTags || []).map((tag) => ({
            tagName: tag.tagName,
        }));

        const exactMatch = existingTags.find((tag) => tag.tagName.toLowerCase() === tagName.toLowerCase());

        return {
            exists: !!exactMatch,
            existingTag: exactMatch,
        };
    };

    findSimilarTags = (tagName) => {
        const existingTags = this.props.existingTags || [];
        const similar = [];

        existingTags.forEach((tag) => {
            const distance = this.levenshteinDistance(tagName.toLowerCase(), tag.tagName.toLowerCase());
            if (distance > 0 && distance <= 2) {
                similar.push(tag.tagName);
            }
        });

        return similar;
    };

    levenshteinDistance = (str1, str2) => {
        const matrix = [];

        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
                }
            }
        }

        return matrix[str2.length][str1.length];
    };

    validateTagsForCreation = (selectedTags) => {
        const valid = [];
        const renamed = [];
        const similar = [];
        const processedTagNames = new Set();

        selectedTags.forEach((tag) => {
            const originalTagName = tag.tagName || tag.suggestedTagName;
            let newTagName = originalTagName;
            let counter = 1;
            let wasRenamed = false;

            // Check if tag already exists or was already processed
            while (this.checkForDuplicates(newTagName).exists || processedTagNames.has(newTagName.toLowerCase())) {
                newTagName = `${originalTagName}-(${counter})`;
                counter++;
                wasRenamed = true;
            }

            processedTagNames.add(newTagName.toLowerCase());

            const processedTag = {
                ...tag,
                tagName: newTagName,
                originalTagName: wasRenamed ? originalTagName : undefined,
                wasRenamed: wasRenamed,
            };

            // Check for similar tags (only for the original name if not exact duplicate)
            if (!wasRenamed) {
                const similarTags = this.findSimilarTags(originalTagName);
                if (similarTags.length > 0) {
                    similar.push({
                        newTag: originalTagName,
                        similar: similarTags,
                        fieldCount: tag.fieldIds.length,
                    });
                }
            }

            if (wasRenamed) {
                renamed.push(processedTag);
            }

            valid.push(processedTag);
        });

        return { valid, renamed, similar };
    };

    getSelectedNewTags = () => {
        const suggestions = this.getSuggestions(this.props.suggestions);
        const selectedMap = new Map();

        suggestions.forEach((fieldSuggestion) => {
            const newTags = fieldSuggestion.newTagRecommendations || [];

            newTags.forEach((tag) => {
                const tagName = tag.tagName || tag.suggestedTagName;
                const isSelected = this.state.selectedSuggestions[fieldSuggestion.fieldId]?.newTagRecommendations?.[tagName];

                if (isSelected && !this.isTagCreated(tagName)) {
                    if (!selectedMap.has(tagName)) {
                        selectedMap.set(tagName, {
                            tagName: tagName,
                            tagDescription: tag.tagDescription || tag.reasoning || "",
                            confidence: tag.confidence,
                            fieldIds: [fieldSuggestion.fieldId],
                        });
                    } else {
                        selectedMap.get(tagName).fieldIds.push(fieldSuggestion.fieldId);
                    }
                }
            });
        });

        return Array.from(selectedMap.values());
    };

    getSelectedNewTagsCount = () => {
        return this.getSelectedNewTags().length;
    };

    isTagCreated = (tagName) => {
        return this.state.createdTags.has(tagName);
    };

    moveCreatedTagsToAdditional = (createdTagNames) => {
        const suggestions = this.getSuggestions(this.props.suggestions);
        const updatedSelections = { ...this.state.selectedSuggestions };

        suggestions.forEach((fieldSuggestion) => {
            const newTags = fieldSuggestion.newTagRecommendations || [];

            newTags.forEach((tag) => {
                const tagName = tag.tagName || tag.suggestedTagName;

                if (createdTagNames.includes(tagName)) {
                    const fieldId = fieldSuggestion.fieldId;
                    if (!updatedSelections[fieldId]) {
                        updatedSelections[fieldId] = {
                            additionalTags: {},
                            mismatchedTags: {},
                            newTagRecommendations: {},
                        };
                    }

                    if (updatedSelections[fieldId].newTagRecommendations[tagName]) {
                        delete updatedSelections[fieldId].newTagRecommendations[tagName];
                    }

                    updatedSelections[fieldId].additionalTags = updatedSelections[fieldId].additionalTags || {};
                    updatedSelections[fieldId].additionalTags[tagName] = true;

                    if (!fieldSuggestion.additionalTags) {
                        fieldSuggestion.additionalTags = [];
                    }

                    const tagExists = fieldSuggestion.additionalTags.some((t) => {
                        const existingTagName = t.tagName || t.suggestedTagName;
                        return existingTagName === tagName;
                    });

                    if (!tagExists) {
                        fieldSuggestion.additionalTags.push({
                            tagName: tagName,
                            confidence: tag.confidence || 1,
                            reasoning: tag.reasoning || `Created from AI suggestion`,
                        });
                    }
                }
            });
        });

        return updatedSelections;
    };

    handleSelectAllNewTags = () => {
        const suggestions = this.getSuggestions(this.props.suggestions);
        const newSelections = { ...this.state.selectedSuggestions };
        let selectedCount = 0;

        suggestions.forEach((fieldSuggestion) => {
            const newTags = fieldSuggestion.newTagRecommendations || [];

            if (newTags.length > 0) {
                if (!newSelections[fieldSuggestion.fieldId]) {
                    newSelections[fieldSuggestion.fieldId] = {
                        additionalTags: {},
                        mismatchedTags: {},
                        newTagRecommendations: {},
                    };
                }

                newTags.forEach((tag) => {
                    const tagName = tag.tagName || tag.suggestedTagName;
                    // Select all new tags - duplicates will be auto-renamed during creation
                    if (!this.isTagCreated(tagName)) {
                        newSelections[fieldSuggestion.fieldId].newTagRecommendations[tagName] = true;
                        selectedCount++;
                    }
                });
            }
        });

        this.setState({ selectedSuggestions: newSelections });

        if (selectedCount > 0) {
            message.success(`${selectedCount} new tag suggestion(s) selected for creation`);
        } else {
            message.info("All new suggestions have already been created");
        }
    };

    toggleFieldExpansion = (fieldId) => {
        this.setState((prevState) => {
            const newExpanded = new Set(prevState.expandedFields);
            if (newExpanded.has(fieldId)) {
                newExpanded.delete(fieldId);
            } else {
                newExpanded.add(fieldId);
            }
            return { expandedFields: newExpanded };
        });
    };

    onSuggestionToggle = (fieldId, category, tagName, checked) => {
        this.setState((prevState) => ({
            selectedSuggestions: {
                ...prevState.selectedSuggestions,
                [fieldId]: {
                    ...prevState.selectedSuggestions[fieldId],
                    [category]: {
                        ...(prevState.selectedSuggestions[fieldId]?.[category] || {}),
                        [tagName]: checked,
                    },
                },
            },
        }));
    };

    handleApply = async () => {
        this.setState({ loading: true });
        try {
            await this.props.onApply(this.state.selectedSuggestions);
        } catch (error) {
            message.error("Failed to apply suggestions. Something went wrong.");
        } finally {
            this.setState({ loading: false });
        }
    };

    handleExpandAll = () => {
        const allFieldIds = this.getFilteredSuggestions().map((s) => s.fieldId);
        this.setState({ expandedFields: new Set(allFieldIds) });
    };

    handleCollapseAll = () => {
        this.setState({ expandedFields: new Set() });
    };

    showCreationConfirmation = () => {
        const selectedNewTags = this.getSelectedNewTags();

        if (selectedNewTags.length === 0) {
            message.warning("Please select at least one tag to create");
            return;
        }

        const { valid, renamed, similar } = this.validateTagsForCreation(selectedNewTags);

        if (valid.length === 0) {
            message.error("No valid tags to create");
            return;
        }

        this.setState({
            showCreationConfirmation: true,
            tagsToCreate: valid,
            duplicatesSkipped: renamed,
            similarWarnings: similar,
        });
    };

    hideCreationConfirmation = () => {
        this.setState({
            showCreationConfirmation: false,
            tagsToCreate: [],
            duplicatesSkipped: [],
            similarWarnings: [],
        });
    };

    hideSuccessPanel = () => {
        this.setState({ showCreationSuccess: false });
    };

    performBulkCreate = async () => {
        const { tagsToCreate } = this.state;
        const { businessAreaId, onBulkCreateTags } = this.props;

        if (!onBulkCreateTags) {
            message.error("Bulk create function not available");
            return;
        }

        this.setState({ isCreatingTags: true });

        const payload = {
            tags: tagsToCreate.map((t) => ({
                tagName: t.tagName || t.suggestedTagName,
                tagDescription: t.tagDescription,
                businessAreaId: businessAreaId,
            })),
        };

        onBulkCreateTags(payload);
    };

    getConfidenceColor = (confidence) => {
        if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return "success";
        if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return "warning";
        return "error";
    };

    getConfidenceDotColor = (confidence) => {
        if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return "bg-green-500";
        if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return "bg-yellow-500";
        return "bg-red-500";
    };

    getTagItemClassName = (isCreated, isDisabled) => {
        const baseClasses = "flex items-start gap-3 p-3 rounded-lg transition-all";

        if (isCreated) {
            return `${baseClasses} bg-green-50 border-2 border-green-400 animate-pulse-once`;
        }
        if (isDisabled) {
            return `${baseClasses} bg-gray-50 border border-gray-200 opacity-60`;
        }
        return `${baseClasses} bg-white border border-gray-200 hover:border-blue-300`;
    };
    renderStatCard = (value, label, bgColor, iconColor, Icon) => (
        <div className={`${bgColor} rounded-xl border p-2 backdrop-blur-sm ${iconColor.replace("text-", "border-")}/30`}>
            <div className="mb-1 flex items-center gap-2">
                {Icon && <Icon className={`${iconColor} text-sm`} />}
                <span className={`text-lg font-bold ${iconColor}`}>{value}</span>
            </div>
            <div className={`text-xs ${iconColor}/80 uppercase tracking-wider`}>{label}</div>
        </div>
    );

    renderTagDetails = (tag, tagName, isDisabled, isCreated, exists) => (
        <>
            <div className="mb-2 flex items-center gap-2">
                <Tag color={this.getConfidenceColor(tag.confidence)} className="rounded-full px-3 py-1 text-sm font-medium">
                    {tagName}
                </Tag>
                <div className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
                    <div className={`h-2 w-2 rounded-full ${this.getConfidenceDotColor(tag.confidence)}`}></div>
                    <span className="text-xs font-medium text-gray-600">{Math.round((tag.confidence || 0) * 100)}%</span>
                    {isDisabled && isCreated && (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                            Created
                        </Tag>
                    )}
                    {isDisabled && exists && (
                        <Tag color="error" icon={<ExclamationCircleOutlined />}>
                            Already Exists
                        </Tag>
                    )}
                </div>
            </div>
            {tag.reasoning && <p className="m-0 rounded-lg bg-gray-50 p-2 text-sm leading-relaxed text-gray-600">{tag.reasoning}</p>}
        </>
    );

    renderNewSuggestionsInfo = () => {
        if (this.state.activeTab !== "new") return null;

        const suggestions = this.getSuggestions(this.props.suggestions);

        let totalUncreatdNewTags = 0;
        suggestions.forEach((fieldSuggestion) => {
            const newTags = fieldSuggestion.newTagRecommendations || [];
            newTags.forEach((tag) => {
                const tagName = tag.tagName || tag.suggestedTagName;
                if (!this.isTagCreated(tagName)) {
                    totalUncreatdNewTags++;
                }
            });
        });

        const selectedCount = this.getSelectedNewTagsCount();
        const selectedNewTags = this.getSelectedNewTags();

        // All selected tags can be created - duplicates will be auto-renamed
        const hasCreatableTags = selectedNewTags.length > 0;

        return (
            <div className="mx-5 mb-3 mt-3 rounded-lg border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 p-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <BulbOutlined className="text-xl text-blue-600" />
                        <div>
                            <h4 className="mb-0.5 text-sm font-bold text-blue-900">Create New Tags</h4>
                            <p className="mb-0 text-xs text-blue-700">
                                {totalUncreatdNewTags > 0
                                    ? `${totalUncreatdNewTags} suggested tag${totalUncreatdNewTags > 1 ? "s" : ""} need${totalUncreatdNewTags === 1 ? "s" : ""} to be created first`
                                    : 'All tags created! Switch to "Additional Tags" to apply them.'}
                            </p>
                        </div>
                    </div>
                    {totalUncreatdNewTags > 0 && (
                        <div className="flex items-center gap-2">
                            {selectedCount > 0 && <span className="text-xs font-semibold text-blue-600">{selectedCount} selected</span>}
                            <Button
                                type="default"
                                size="small"
                                icon={<CheckCircleOutlined />}
                                onClick={this.handleSelectAllNewTags}
                                className="border-blue-400 text-blue-600 hover:bg-blue-50"
                            >
                                Select All
                            </Button>
                            {hasCreatableTags && (
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={this.showCreationConfirmation}
                                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                                >
                                    Create {selectedNewTags.length} Tag{selectedNewTags.length > 1 ? "s" : ""}
                                </Button>
                            )}
                        </div>
                    )}
                    {totalUncreatdNewTags === 0 && this.state.justCreatedTagNames.length > 0 && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={() => this.setState({ activeTab: "additional" })}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            Go to Additional Tags
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    renderCreationConfirmationPanel = () => {
        const { showCreationConfirmation, tagsToCreate, duplicatesSkipped, similarWarnings, isCreatingTags } = this.state;

        if (!showCreationConfirmation) return null;

        return (
            <div className="animate-slideDown absolute inset-x-0 top-1/3 z-50 border-t-4 border-blue-500 bg-white shadow-2xl">
                <div className="max-h-[90vh] overflow-y-auto">
                    <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                                    <PlusOutlined className="text-2xl text-white" />
                                </div>
                                <div>
                                    <h3 className="mb-1 text-xl font-bold text-gray-900">
                                        Ready to Create New Tags in <span className="font-semibold text-blue-600">{this.props.businessAreaName}</span>
                                    </h3>
                                </div>
                            </div>
                            <Button
                                type="text"
                                icon={<span className="text-xl text-gray-400">✕</span>}
                                onClick={this.hideCreationConfirmation}
                                disabled={isCreatingTags}
                                className="h-10 w-10 rounded-lg hover:bg-gray-100"
                            />
                        </div>

                        {/* Tags to Create */}
                        <div className="mb-5">
                            <div className="mb-3 flex items-center justify-between">
                                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-800">📋 Tags to Create</h4>
                                <Badge count={tagsToCreate.length} style={{ backgroundColor: "#3b82f6" }} className="font-semibold" />
                            </div>
                            <div className="max-h-60 space-y-3 overflow-y-auto rounded-xl border-2 border-gray-200 bg-white p-4 shadow-sm">
                                {tagsToCreate.map((tag, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex items-start gap-3 rounded-lg border p-3 transition-all hover:shadow-md ${
                                            tag.wasRenamed
                                                ? "border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50"
                                                : "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50"
                                        }`}
                                    >
                                        <div className="mt-1">
                                            {tag.wasRenamed ? <WarningOutlined className="text-lg text-orange-600" /> : <CheckCircleOutlined className="text-lg text-green-600" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="mb-1 flex items-center gap-2">
                                                <span className={`text-base font-bold ${tag.wasRenamed ? "text-orange-900" : "text-gray-900"}`}>{tag.tagName}</span>
                                                {tag.wasRenamed && (
                                                    <Tooltip
                                                        title={
                                                            <div className="text-xs">
                                                                <div className="mb-1">Auto-renamed to avoid duplicate</div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-gray-300 line-through">{tag.originalTagName}</span>
                                                                    <ArrowRightOutlined className="text-xs" />
                                                                    <span className="font-medium text-white">{tag.tagName}</span>
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <Tag className="m-0 flex items-center border-orange-300 bg-orange-100 px-2 py-0.5 text-orange-700">
                                                            <WarningOutlined className="mr-1 text-xs" />
                                                            <span className="text-xs font-semibold">Renamed</span>
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                                {tag.fieldIds && tag.fieldIds.length > 1 && (
                                                    <Tooltip title={`Suggested by ${tag.fieldIds.length} fields`}>
                                                        <Tag color="purple" className="text-xs">
                                                            {tag.fieldIds.length} fields
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {tag.tagDescription && <p className="m-0 text-sm leading-relaxed text-gray-700">{tag.tagDescription}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Warnings Section */}
                        {(duplicatesSkipped.length > 0 || similarWarnings.length > 0) && (
                            <div className="mb-5 space-y-3">
                                {duplicatesSkipped.length > 0 && (
                                    <div className="rounded-lg border-l-4 border-orange-500 bg-gradient-to-r from-orange-50 to-amber-50 p-4 shadow-sm">
                                        <div className="flex items-start gap-3">
                                            <WarningOutlined className="mt-0.5 flex-shrink-0 text-xl text-orange-600" />
                                            <div className="flex-1">
                                                <p className="mb-2 text-sm font-bold text-orange-900">🔄 {duplicatesSkipped.length} tag(s) auto-renamed to avoid duplicates</p>
                                                <div className="space-y-1">
                                                    {duplicatesSkipped.map((t, i) => (
                                                        <div key={i} className="flex items-center gap-2 text-xs text-orange-800">
                                                            <span className="line-through opacity-60">{t.originalTagName}</span>
                                                            <ArrowRightOutlined className="text-xs" />
                                                            <Tag color="orange" className="m-0 font-semibold">
                                                                {t.tagName}
                                                            </Tag>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {similarWarnings.length > 0 && (
                                    <div className="rounded-lg border-l-4 border-yellow-500 bg-gradient-to-r from-yellow-50 to-amber-50 p-4 shadow-sm">
                                        <div className="flex items-start gap-3">
                                            <WarningOutlined className="mt-0.5 flex-shrink-0 text-xl text-yellow-600" />
                                            <div className="flex-1">
                                                <p className="mb-2 text-sm font-bold text-yellow-900">💡 Similar tags detected</p>
                                                <ul className="ml-0 list-none space-y-2 text-xs text-yellow-800">
                                                    {similarWarnings.map((w, i) => (
                                                        <li key={i} className="flex items-start gap-2">
                                                            <span className="font-semibold">"{w.newTag}"</span>
                                                            <span className="text-yellow-700">is similar to:</span>
                                                            <span className="font-medium">{w.similar.join(", ")}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between border-t-2 border-gray-200 pt-5">
                            <p className="text-xs italic text-gray-600">💡 Duplicate tags will be automatically renamed with a suffix like "TagName-(1)"</p>
                            <div className="flex gap-3">
                                <Button onClick={this.hideCreationConfirmation} disabled={isCreatingTags} size="large" className="px-6">
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={isCreatingTags ? <Spin size="small" /> : <CheckCircleOutlined />}
                                    onClick={this.performBulkCreate}
                                    loading={isCreatingTags}
                                    size="large"
                                    className="border-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-8 shadow-lg hover:from-blue-700 hover:to-indigo-700"
                                >
                                    {isCreatingTags ? "Creating..." : `Create ${tagsToCreate.length} Tag${tagsToCreate.length > 1 ? "s" : ""}`}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderSuccessPanel = () => {
        const { showCreationSuccess, justCreatedTagNames } = this.state;

        if (!showCreationSuccess) return null;

        return (
            <div className="animate-slideDown absolute inset-x-0 top-1/3 z-50 border-t-4 border-green-500 bg-white shadow-2xl">
                <div className="animate-slideDown border-t-4 border-green-500 bg-gradient-to-br from-green-50 via-emerald-50 to-white p-6 shadow-lg">
                    <div className="mb-5 flex items-start gap-4">
                        <div className="relative">
                            <div className="animate-bounce-once flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-400 to-emerald-600 shadow-xl">
                                <CheckCircleOutlined className="text-3xl text-white" />
                            </div>
                            <div className="absolute -right-1 -top-1 h-5 w-5 animate-ping rounded-full bg-yellow-400"></div>
                        </div>

                        <div className="flex-1">
                            <h3 className="mb-2 flex items-center gap-2 text-2xl font-bold text-gray-900">Success! 🎉</h3>
                            <p className="mb-3 text-base text-gray-700">
                                <span className="font-bold text-green-600">
                                    {justCreatedTagNames.length} tag{justCreatedTagNames.length > 1 ? "s" : ""}
                                </span>{" "}
                                created successfully in <span className="font-semibold">{this.props.businessAreaName}</span>
                            </p>

                            <div className="mb-4 rounded-lg border border-green-200 bg-white p-3">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Created Tags:</p>
                                <div className="flex flex-wrap gap-2">
                                    {justCreatedTagNames.map((tagName, idx) => (
                                        <Tag key={idx} color="success" icon={<CheckCircleOutlined />} className="px-3 py-1 text-sm font-medium">
                                            {tagName}
                                        </Tag>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded border-l-4 border-blue-400 bg-blue-50 p-3">
                                <p className="mb-1 text-sm font-semibold text-blue-900">📌 Next Steps:</p>
                                <p className="mb-0 text-sm text-blue-800">
                                    🪄 Voila!! The newly created tags are auto-applied to the relevant fields under the "Additional Tags" section. You can review and adjust them as
                                    needed.
                                </p>
                            </div>
                        </div>

                        <Button
                            type="text"
                            icon={<span className="text-xl text-gray-400">✕</span>}
                            onClick={this.hideSuccessPanel}
                            className="h-10 w-10 flex-shrink-0 rounded-lg hover:bg-gray-100"
                        />
                    </div>

                    <div className="flex justify-end border-t border-gray-200 pt-4">
                        <Button type="primary" onClick={this.hideSuccessPanel} size="large" className="border-0 bg-gradient-to-r from-green-600 to-emerald-600 px-8">
                            Got it, thanks!
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    renderTabFilters = () => {
        const counts = this.getCategoryCounts();
        const { activeTab } = this.state;

        const extraContent = (
            <div className="mb-1 ml-4 flex items-center rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                <Tooltip title="Expand all suggestion cards">
                    <button
                        onClick={this.handleExpandAll}
                        className="rounded-md p-1.5 text-gray-500 outline-none transition-all duration-200 hover:bg-gray-100 hover:text-blue-600"
                    >
                        <PlusSquareOutlined className="align-middle text-base" />
                    </button>
                </Tooltip>
                <div className="mx-1 h-4 w-px bg-gray-300"></div>
                <Tooltip title="Collapse all suggestion cards">
                    <button
                        onClick={this.handleCollapseAll}
                        className="rounded-md p-1.5 text-gray-500 outline-none transition-all duration-200 hover:bg-gray-100 hover:text-blue-600"
                    >
                        <MinusSquareOutlined className="align-middle text-base" />
                    </button>
                </Tooltip>
            </div>
        );

        const tabItems = [
            {
                key: "all",
                label: (
                    <Tooltip title="All fields with any type of suggestion">
                        <div className="flex items-center gap-2">
                            <FilterOutlined className={`text-lg ${activeTab === "all" ? "text-blue-600" : ""}`} />
                            <span className={activeTab === "all" ? "font-bold text-blue-600" : ""}>All Fields</span>
                            <Badge count={counts.all} showZero color={activeTab === "all" ? "#1890ff" : "#9ca3af"} />
                        </div>
                    </Tooltip>
                ),
            },
            {
                key: "additional",
                label: (
                    <Tooltip title="Fields with recommended additional tags to improve classification">
                        <div className="flex items-center gap-2">
                            <PlusOutlined className={`text-lg ${activeTab === "additional" ? "text-green-600" : ""}`} />
                            <span className={activeTab === "additional" ? "font-bold text-green-700" : ""}>Additional Tags</span>
                            <Badge count={counts.additional} showZero color={activeTab === "additional" ? "#52c41a" : "#9ca3af"} />
                        </div>
                    </Tooltip>
                ),
            },
            {
                key: "mismatched",
                label: (
                    <Tooltip title="Tags that don't match field content well - check tags you want to remove">
                        <div className="flex items-center gap-2">
                            <ExclamationCircleOutlined className={`text-lg ${activeTab === "mismatched" ? "text-yellow-600" : ""}`} />
                            <span className={activeTab === "mismatched" ? "font-bold text-yellow-700" : ""}>Incorrect Tags</span>
                            <Badge count={counts.mismatched} showZero color={activeTab === "mismatched" ? "#faad14" : "#9ca3af"} />
                        </div>
                    </Tooltip>
                ),
            },
            {
                key: "new",
                label: (
                    <Tooltip title="AI-generated suggestions for new tags based on content analysis">
                        <div className="flex items-center gap-2">
                            <BulbOutlined className={`text-lg ${activeTab === "new" ? "text-blue-600" : ""}`} />
                            <span className={activeTab === "new" ? "font-bold text-blue-700" : ""}>New Suggestions</span>
                            <Badge count={counts.new} showZero color={activeTab === "new" ? "#1890ff" : "#9ca3af"} />
                        </div>
                    </Tooltip>
                ),
            },
        ];

        return (
            <div className="border-b border-gray-200 bg-slate-100 px-5 pt-3">
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => this.setState({ activeTab: key })}
                    className="suggestion-tabs"
                    type="card"
                    items={tabItems}
                    tabBarStyle={{ marginBottom: -1 }}
                    tabBarExtraContent={{ right: extraContent }}
                />
            </div>
        );
    };

    renderTagSection = (title, tags, icon, bgColor, borderColor, textColor, category, fieldId, isCheckable = true) => {
        if (!tags || !Array.isArray(tags) || tags.length === 0) return null;

        const filteredTags =
            category === "newTagRecommendations"
                ? tags.filter((tag) => {
                      const tagName = tag.tagName || tag.suggestedTagName;
                      return !this.isTagCreated(tagName);
                  })
                : tags;

        if (filteredTags.length === 0) return null;

        return (
            <div className={`${bgColor} mb-4 rounded-xl border p-4 ${borderColor} transition-all duration-300 hover:scale-[1.01] hover:shadow-lg`}>
                <div className="mb-3 flex items-center gap-3">
                    {icon}
                    <h5 className={`text-sm font-semibold ${textColor} mb-0`}>{title}</h5>
                    <Badge count={tags.length} showZero={false} size="small" className="bg-white" />
                </div>

                <div className="space-y-3">
                    {tags.map((tag, index) => {
                        const tagName = tag.tagName || tag.suggestedTagName;
                        const uniqueKey = `${fieldId}-${category}-${tagName}-${index}`;
                        const isSelected = this.state.selectedSuggestions[fieldId]?.[category]?.[tagName];
                        const isCreated = this.isTagCreated(tagName);
                        const { exists } = this.checkForDuplicates(tagName);
                        const isDisabled = category === "newTagRecommendations" && (exists || isCreated);

                        return (
                            <div
                                key={uniqueKey}
                                className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md"
                            >
                                <div className="flex items-start justify-between">
                                    <div className={this.getTagItemClassName(isCreated, isDisabled)}>
                                        {isCheckable && (
                                            <Tooltip
                                                title={
                                                    category === "mismatchedTags"
                                                        ? isSelected
                                                            ? "This tag will be removed from the field"
                                                            : "Check to remove this tag"
                                                        : category === "additionalTags"
                                                          ? isSelected
                                                              ? "This tag will be added to the field"
                                                              : "Check to add this tag"
                                                          : isSelected
                                                            ? "This tag will be created"
                                                            : "Check to create this tag"
                                                }
                                                placement="left"
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => this.onSuggestionToggle(fieldId, category, tagName, e.target.checked)}
                                                    disabled={isDisabled}
                                                    className="mt-1"
                                                />
                                            </Tooltip>
                                        )}
                                        <div className="flex-1">{this.renderTagDetails(tag, tagName, isDisabled, isCreated, exists)}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    renderMismatchedTagsInfo = () => {
        if (this.state.activeTab !== "mismatched") return null;

        return (
            <div className="mx-5 mb-3 mt-3 rounded-lg border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50 p-3 shadow-sm">
                <div className="flex items-start gap-3">
                    <ExclamationCircleOutlined className="text-xl text-yellow-600" />
                    <div className="flex-1">
                        <h4 className="mb-0.5 text-sm font-bold text-yellow-900">Review Incorrect Tags</h4>
                        <p className="mb-0 text-xs text-yellow-800">
                            These tags don't match the field content well. <strong>Check the tags you want to remove</strong> when you apply changes. Unchecked tags will be kept.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    renderSuggestions = () => {
        const suggestions = this.getFilteredSuggestions();
        const { expandedFields, activeTab } = this.state;

        if (!suggestions || suggestions.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
                        <TagOutlined className="text-2xl text-gray-400" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-600">No results found</h3>
                    <p className="text-sm text-gray-500">{EMPTY_MESSAGES[activeTab]}</p>
                </div>
            );
        }

        return (
            <div className="space-y-4 pb-2">
                {suggestions.map((suggestion, index) => {
                    const isExpanded = expandedFields.has(suggestion.fieldId);
                    const additionalTags = suggestion.additionalTags || [];
                    const mismatchedTags = suggestion.mismatchedTags || [];
                    const newTagRecommendations = suggestion.newTagRecommendations || [];

                    const totalSuggestions = additionalTags.length + mismatchedTags.length + newTagRecommendations.length;
                    const hasRecommendations = totalSuggestions > 0;

                    const config = TAG_SECTION_CONFIG;

                    return (
                        <div
                            key={suggestion.fieldId || index}
                            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                        >
                            <div
                                className={`p-2 ${hasRecommendations ? "cursor-pointer bg-gradient-to-r from-slate-50 to-gray-50 hover:from-slate-100 hover:to-gray-100" : "cursor-default bg-gray-50"} border-b border-gray-100 transition-all duration-200`}
                                onClick={() => hasRecommendations && this.toggleFieldExpansion(suggestion.fieldId)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                                            <TagOutlined className="text-lg text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="mb-1 text-lg font-bold text-gray-800">{suggestion.fieldName}</h4>
                                            <p className="mb-0 text-sm text-gray-500">
                                                {hasRecommendations
                                                    ? `${totalSuggestions} suggestion${totalSuggestions !== 1 ? "s" : ""} available`
                                                    : "No recommendations available"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {hasRecommendations && (
                                            <>
                                                <div className="flex gap-2">
                                                    {additionalTags.length > 0 && (
                                                        <Tooltip title="Additional tags to add">
                                                            <div className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1">
                                                                <PlusOutlined className="text-xs text-green-600" />
                                                                <span className="text-xs font-medium text-green-700">{additionalTags.length}</span>
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                    {mismatchedTags.length > 0 && (
                                                        <Tooltip title="Mismatched tags to review">
                                                            <div className="flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1">
                                                                <DeleteOutlined className="text-xs text-yellow-600" />
                                                                <span className="text-xs font-medium text-yellow-700">{mismatchedTags.length}</span>
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                    {newTagRecommendations.length > 0 && (
                                                        <Tooltip title="New tag suggestions">
                                                            <div className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1">
                                                                <BulbOutlined className="text-xs text-blue-600" />
                                                                <span className="text-xs font-medium text-blue-700">{newTagRecommendations.length}</span>
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 transition-colors duration-200 hover:bg-gray-200">
                                                    {isExpanded ? <UpOutlined className="text-sm text-gray-600" /> : <DownOutlined className="text-sm text-gray-600" />}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}>
                                <div className="space-y-4 bg-gray-50 p-3">
                                    {this.renderTagSection(
                                        config.additionalTags.title,
                                        additionalTags,
                                        config.additionalTags.icon,
                                        config.additionalTags.bgColor,
                                        config.additionalTags.borderColor,
                                        config.additionalTags.textColor,
                                        "additionalTags",
                                        suggestion.fieldId,
                                        true,
                                    )}
                                    {this.renderTagSection(
                                        config.mismatchedTags.title,
                                        mismatchedTags,
                                        config.mismatchedTags.icon,
                                        config.mismatchedTags.bgColor,
                                        config.mismatchedTags.borderColor,
                                        config.mismatchedTags.textColor,
                                        "mismatchedTags",
                                        suggestion.fieldId,
                                        true,
                                    )}
                                    {this.renderTagSection(
                                        config.newTagRecommendations.title,
                                        newTagRecommendations,
                                        config.newTagRecommendations.icon,
                                        config.newTagRecommendations.bgColor,
                                        config.newTagRecommendations.borderColor,
                                        config.newTagRecommendations.textColor,
                                        "newTagRecommendations",
                                        suggestion.fieldId,
                                        true,
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    render() {
        const { visible, onCancel, suggestions } = this.props;
        const { loading, showCreationConfirmation, showCreationSuccess, activeTab } = this.state;
        const summary = this.getSummary(suggestions);

        const selectedNewTags = this.getSelectedNewTags();
        // All selected tags can be created - duplicates will be auto-renamed
        const hasCreatableTags = selectedNewTags.length > 0;

        const handleCancel = () => {
            this.resetAllStates();
            onCancel();
        };

        return (
            <Modal
                title={
                    <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
                        <div className="bg-grid-white/[0.05] absolute inset-0 bg-[size:20px_20px]"></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/20"></div>
                        <div className="relative px-6 py-4">
                            <div className="mb-5 flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 shadow-lg">
                                            <TagOutlined className="text-xl text-white" />
                                        </div>
                                        <div className="absolute -right-1 -top-1 h-4 w-4 animate-pulse rounded-full border-2 border-white bg-green-400"></div>
                                    </div>
                                    <div>
                                        <h2 className="mb-2 text-lg font-bold tracking-tight text-white">Auto-Tagging Intelligence</h2>
                                        <p className="text-sm leading-relaxed text-slate-300">AI-powered recommendations to enhance your data classification</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleCancel}
                                    className="group flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 transition-all duration-200 hover:bg-white/20"
                                >
                                    <span className="text-white transition-transform duration-200 group-hover:scale-110">✕</span>
                                </button>
                            </div>
                            {summary && (
                                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                    {this.renderStatCard(summary.totalFields, "Fields Analyzed", "bg-white/10", "text-white")}

                                    {summary.fieldsWithAdditionalTags > 0 &&
                                        this.renderStatCard(summary.fieldsWithAdditionalTags, "Ready to Enhance", "bg-emerald-500/20", "text-emerald-400", PlusOutlined)}

                                    {summary.fieldsWithMismatchedTags > 0 &&
                                        this.renderStatCard(summary.fieldsWithMismatchedTags, "Need Review", "bg-amber-500/20", "text-amber-400", ExclamationCircleOutlined)}

                                    {summary.fieldsWithNewTagRecommendations > 0 &&
                                        this.renderStatCard(summary.fieldsWithNewTagRecommendations, "New Suggestions", "bg-purple-500/20", "text-purple-400", BulbOutlined)}
                                </div>
                            )}
                        </div>
                    </div>
                }
                open={visible}
                destroyOnHidden
                onClose={onCancel}
                onCancel={onCancel}
                width={800}
                maskClosable={false}
                centered
                closable={false}
                styles={{
                    header: { padding: 0, margin: 0, borderBottom: "none" },
                    body: { padding: 0, height: "500px", display: "flex", flexDirection: "column", overflow: "hidden" },
                    content: { padding: 0, overflow: "hidden", borderRadius: "16px", maxHeight: "90vh", display: "flex", flexDirection: "column" },
                }}
                className="[&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-2xl [&_.ant-modal-content]:!p-0 [&_.ant-modal-header]:!m-0 [&_.ant-modal-header]:!bg-transparent [&_.ant-modal-header]:!p-0"
                footer={
                    <div>
                        {this.renderCreationConfirmationPanel()}
                        {this.renderSuccessPanel()}
                        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
                            <div className="text-xs text-gray-600">
                                {activeTab === "all" && "Review and apply tag recommendations"}
                                {activeTab === "additional" && "Select tags to add to fields"}
                                {activeTab === "mismatched" && <span className="font-medium text-yellow-700">⚠️ Checked tags will be removed from fields</span>}
                                {activeTab === "new" && hasCreatableTags && (
                                    <span className="flex items-center gap-2 text-blue-700">
                                        <BulbOutlined />
                                        <span className="font-medium">
                                            {selectedNewTags.length} tag{selectedNewTags.length > 1 ? "s" : ""} ready to create
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button key="cancel" onClick={handleCancel} size="middle" className="px-5">
                                    Cancel
                                </Button>
                                <Button
                                    key="apply"
                                    type="primary"
                                    onClick={this.handleApply}
                                    loading={loading}
                                    size="middle"
                                    icon={<CheckCircleOutlined />}
                                    className="border-0 bg-gradient-to-r from-blue-600 to-purple-600 px-6 hover:from-blue-700 hover:to-purple-700"
                                >
                                    Apply Changes
                                </Button>
                            </div>
                        </div>
                    </div>
                }
            >
                <div className="flex h-full flex-col">
                    <div className="flex-shrink-0">
                        {this.renderTabFilters()}
                        {this.renderNewSuggestionsInfo()}
                        {this.renderMismatchedTagsInfo()}
                    </div>

                    <div
                        className={`scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 flex-1 overflow-y-auto px-5 pb-3 pt-2 transition-all duration-300 ${showCreationConfirmation || showCreationSuccess ? "pointer-events-none opacity-30" : "opacity-100"}`}
                        style={{ minHeight: 0 }}
                    >
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Spin size="large" />
                                <p className="mt-4 text-lg text-gray-600">Applying changes...</p>
                            </div>
                        ) : (
                            this.renderSuggestions()
                        )}
                    </div>
                </div>
            </Modal>
        );
    }
}

export default React.memo(AutoTaggingModal);
